/* =============================================================================
 * motion.js — the scroll behaviour behind the page.
 *
 * Deliberately dependency-free. The reference site (ideapeddler.com) runs
 * Webflow's IX2 engine, which for reveals is nothing more than an intersection
 * observer flipping a class that drives a translate3d + opacity transition.
 * That is reproduced here in about forty lines, which is a much better trade
 * than shipping GSAP + ScrollTrigger (~70KB gzipped) to a landing page.
 *
 * Every hook here checks prefers-reduced-motion and degrades to a static,
 * fully-visible page rather than an empty one. A reveal that never fires is a
 * blank page, so the failure mode is handled explicitly in each case.
 * ===========================================================================*/

import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Reveals elements carrying `.reveal` inside the returned ref as they enter
 * the viewport, staggering siblings that share a `data-stagger` parent.
 *
 * Reveal is one-way on purpose: re-hiding on scroll-up reads as a glitch, and
 * it makes ctrl-F and screen readers behave badly.
 */
export function useRevealRoot() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll('.reveal'));
    if (targets.length === 0) return undefined;

    const showAll = () => targets.forEach((el) => el.classList.add('is-in'));

    // No observer support, or the visitor asked for less motion: leave the
    // page in its default visible state and do nothing else.
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    // Only NOW is it safe to hide anything: we have an observer to put it
    // back with. Until this line runs the page is fully readable, so a script
    // that fails to load or parse cannot blank the site.
    document.documentElement.classList.add('js-reveal');

    // Anything already on screen at mount is revealed on the next frame
    // rather than waiting for the observer's first callback. This covers a
    // deep link (statme.us#safety), a restored scroll position, and a reload
    // partway down the page.
    const raf = window.requestAnimationFrame(() => {
      targets.forEach((el) => {
        const box = el.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) {
          el.classList.add('is-in');
        }
      });
    });

    // Belt and braces. If the observer has not reported anything within a few
    // seconds (a throttled tab, a paused rendering pipeline, a browser quirk),
    // show everything. A missed animation is a rounding error; a blank page
    // is the whole site.
    const net = window.setTimeout(showAll, 2500);

    // Stagger is assigned per group so a row of cards cascades rather than
    // landing all at once. 70ms reads as intentional; past ~120ms it drags.
    const groups = new Map();
    targets.forEach((el) => {
      const parent = el.closest('[data-stagger]');
      if (!parent) return;
      const list = groups.get(parent) || [];
      list.push(el);
      groups.set(parent, list);
    });
    groups.forEach((list) => {
      list.forEach((el, i) => {
        el.style.setProperty('--reveal-delay', `${i * 70}ms`);
      });
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      // Fire slightly before the element is fully on screen so the motion
      // finishes about when the reader's eye arrives.
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    targets.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      window.cancelAnimationFrame(raf);
      window.clearTimeout(net);
    };
  }, []);

  return rootRef;
}

/** True once the page has scrolled past `offset` pixels. Drives the nav. */
export function useScrolledPast(offset = 40) {
  const [past, setPast] = useState(false);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      setPast(window.scrollY > offset);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [offset]);

  return past;
}

/**
 * Counts a number up once its element is on screen. Used for the figures row.
 * Falls back to the final value instantly under reduced motion, so the number
 * is always correct even when nothing animates.
 */
export function useCountUp(target, durationMs = 1100) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setValue(target);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || done.current) return;
          done.current = true;
          io.disconnect();

          const start = performance.now();
          const step = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            // Same out-expo shape as the CSS easing, so the numbers settle
            // on the same rhythm as the panels they sit in.
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(target * eased);
            if (t < 1) window.requestAnimationFrame(step);
            else setValue(target);
          };
          window.requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 },
    );

    io.observe(el);

    // Same safety net as the reveal hook: if the observer never reports, snap
    // to the real number. A figure stuck on 0 is worse than one that did not
    // animate, because it is WRONG rather than merely static.
    const net = window.setTimeout(() => {
      if (done.current) return;
      done.current = true;
      io.disconnect();
      setValue(target);
    }, 2500);

    return () => {
      io.disconnect();
      window.clearTimeout(net);
    };
  }, [target, durationMs]);

  return [ref, value];
}

/**
 * Runs `fn` on an interval only while the element is on screen, and never
 * under reduced motion. The live-tracker demo uses this so an offscreen
 * animation is not burning a timer on a phone.
 */
export function useVisibleInterval(fn, ms) {
  const ref = useRef(null);
  const saved = useRef(fn);
  saved.current = fn;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    let timer = null;
    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (timer) return;
      timer = window.setInterval(() => saved.current(), ms);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
      },
      { threshold: 0.35 },
    );
    io.observe(el);

    // A backgrounded tab throttles timers unevenly; pausing outright keeps
    // the demo in a sane state when the visitor comes back.
    const onVisibility = () => (document.hidden ? stop() : null);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [ms]);

  return ref;
}

export { prefersReducedMotion };
